import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { Spinner } from "react-bootstrap";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

import { simReportApi } from "../../../../utility/api/apiList";

import {
  showDangerAlert,
  showSuccessAlert,
  showInfoAlert,
} from "../../../../redux/actions/alert/alertActions";

import userMessageType from "../../../../redux/actions/userMessage/userMessageType";

import UploadImageSection from "./uploadImageSection";
import ButtonComponent from "../../button";

import { StyledTooltip } from "../../../../style/base/base";

import {
  StyledContainer,
  StyledForm,
  ActivityButton,
} from "./styledLabReport";

const LabReport = ({
  simID,
  isLogin,
  loading,
  updateData,
  editorContent,
  simReportData,
  uploadedImages,
  setLabReportSection,
}) => {
  const [uploaderImageArray, setUploaderImageArray] = useState(uploadedImages);
  const [uploaderCounter, setUploaderCounter] = useState(1);
  const dispatch = useDispatch();
  const location = useLocation();

  const schema = yup.object().shape({
    content: yup.string().default(simReportData?.content).required(),
    observation: yup.string().default(simReportData?.observation).required(),
    conclusion: yup.string().default(simReportData?.conclusion).required(),
  });

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const handleUploadImages = async (imageID, formValues) => {
    await Promise.all(
      uploaderImageArray.map(async (imageObject) => {
        if (!imageObject.is_uploaded_server) {
          formValues.append("src", imageObject.file);
          formValues.append("type", 0);
          await updateData.uploadImageRun(formValues).then((reponse) => {
            imageID.push(reponse.data.id);
          });
          formValues.delete("src");
          formValues.delete("type");
          return imageID;
        } else {
          imageID.push(imageObject.id);
        }
      })
    );
    return imageID;
  };

  const submitEditorValues = (postValue) => {
    (simReportData?.content
      ? updateData.putLabReportRun(
          postValue,
          null,
          `${simReportApi}${simReportData?.id}/`
        )
      : updateData.postLabReportRun(postValue)
    )
      .then(() => {
        updateData.simReportRun().then(() => {
          if (location.state?.editFromProfile) {
            dispatch(
              showInfoAlert(
                "تغییرات گزارش‌کار ثبت شد. شما می‌توانید تغییرات را در منو پروفایل قسمت گزارش فعالیتها مشاهده کنید."
              )
            );
          } else {
            dispatch(showSuccessAlert("گزارش شما با موفقیت ثبت شد."));
          }
          dispatch({ type: userMessageType.UPDATE_COUNT_UNREAD_MESSAGE });
        });
      })
      .catch(() => {
        dispatch(showDangerAlert("لطفا بعدا دوباره امتحان کنید."));
        dispatch({ type: userMessageType.UPDATE_COUNT_UNREAD_MESSAGE });
      });
  };

  const onSubmit = (data) => {
    //send images
    let formdata = new FormData();
    let image_ids = [];
    let postValue = {
      ...data,
      image_ids: [],
      sim: simID,
      percent: 0,
    };

    if (uploaderImageArray.length > 0) {
      handleUploadImages(image_ids, formdata).then((response) => {
        postValue.image_ids = response;
        submitEditorValues(postValue);
      });
    } else {
      submitEditorValues(postValue);
    }
  };

  return (
    <StyledContainer className="mx-auto p-0 d-flex flex-column align-items-start">
      {simReportData?.content ? (
        <ActivityButton
          onClick={() => simReportData?.finish_at && setLabReportSection(false)}
          className="mx-2 mx-md-0 mb-1 mb-md-0 align-self-md-end"
          $smallButton={true}
          disabled={!isLogin}
        >
          گزارش فعالیت شما
        </ActivityButton>
      ) : (
        <StyledTooltip title="هنوز گزارشی ثبت نکرده‌اید" placement="top">
          <span className="align-self-md-end">
            <ActivityButton
              className="mx-2 mx-md-0 mb-1 mb-md-0"
              $smallButton={true}
              disabled
            >
              گزارش فعالیت شما
            </ActivityButton>
          </span>
        </StyledTooltip>
      )}

      <StyledForm onSubmit={handleSubmit(onSubmit)} className="w-100">
        {editorContent.map((item) => (
          <Controller
            key={item.id}
            control={control}
            name={`${item.name}`}
            render={({ field }) => {
              const { onChange } = field;
              return (
                <>
                  <label className="align-self-start">{item.label}</label>
                  <CKEditor
                    data={item.value && `${item.value}`}
                    disabled={!isLogin}
                    editor={ClassicEditor}
                    onChange={(event, editor) => {
                      onChange(editor.getData());
                    }}
                    config={{
                      language: "ar",
                      toolbar: [
                        "heading",
                        "|",
                        "bold",
                        "italic",
                        "blockQuote",
                        "link",
                        "numberedList",
                        "bulletedList",
                        "insertTable",
                        "tableColumn",
                        "tableRow",
                        "mergeTableCells",
                        "|",
                        "undo",
                        "redo",
                        "insertImage",
                      ],
                    }}
                  />
                </>
              );
            }}
          />
        ))}
        <>
          <UploadImageSection
            isLogin={isLogin}
            updateData={updateData}
            uploaderImageArray={uploaderImageArray}
            setUploaderImageArray={setUploaderImageArray}
            uploaderCounter={uploaderCounter}
            setUploaderCounter={setUploaderCounter}
          />
          <ButtonComponent
            type="submit"
            className="submit-button mt-4 d-block mx-auto"
            disabled={!isValid || !isLogin}
          >
            {loading.uploadImageLoading ||
            loading.postLabReportLoading ||
            loading.putLabReportLoading ? (
              <Spinner
                as="span"
                animation="border"
                role="status"
                aria-hidden="true"
              />
            ) : (
              " ثبت گزارش"
            )}
          </ButtonComponent>
        </>
      </StyledForm>
    </StyledContainer>
  );
};

export default LabReport;
